/* eslint-disable react/prop-types */
import {
  useGlobals,
  useFocusTrap,
  useOnClickOutside,
  useDisableScroll,
  useIsGlobetrotter,
} from '@bwoty-web/ui-kit/hooks';
import {
  ICON_UI_CHEVRON_RIGHT,
  ICON_UI_SEARCH,
  ICON_UI_CLOSE,
} from '@bwoty-web/ui-kit/icons';
import { Icon } from '@bwoty-web/ui-kit';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useState, useRef, useEffect } from 'react';
import SubMenu from './SubMenu';
import './menu.scss';
import useScrollFade from '../../../utils/hooks/useScrollFade';

const Menu = ({ isOpen, onClose, openSearch }) => {
  const [activeItems, setActiveItems] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const overlayRef = useRef(null);
  const containerRef = useRef(null);
  const { content } = useGlobals();
  const isGlobetrotter = useIsGlobetrotter();
  const fadeRef = useRef(null);

  useEffect(() => {
    if (isOpen && !isLoaded) {
      setIsLoaded(true);
    }
  }, [isOpen, isLoaded]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscapeKeyPress = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleEscapeKeyPress);
    return () => {
      window.removeEventListener('keydown', handleEscapeKeyPress);
    };
  }, [isOpen, onClose]);

  useDisableScroll(isOpen);
  useFocusTrap(overlayRef, isOpen);
  useOnClickOutside(containerRef, () => onClose(), isOpen);
  useScrollFade(fadeRef);

  let mappedLinks = [];
  if (isGlobetrotter) mappedLinks = content.mainLinks;
  else {
    mappedLinks = content?.navigationLinks?.map((navLink) => {
      const mappedItem = {
        id: navLink.id,
        title:
          navLink.alternativeText.charAt(0).toUpperCase() +
          navLink.alternativeText.slice(1).toLowerCase(),
      };

      if (!navLink.subLinks || navLink.subLinks.length === 0) {
        mappedItem.link = {
          itemKey: 'link',
          title:
            navLink.alternativeText.charAt(0).toUpperCase() +
            navLink.alternativeText.slice(1).toLowerCase(),
          href: {
            id: 0,
            name: navLink.mainLink.name || '',
            linkval: null,
            target: '_self',
            url: navLink.mainLink.href,
          },
        };
      }
      if (navLink.subLinks && navLink.subLinks.length > 0) {
        mappedItem.subLinks = navLink.subLinks.map((subLink) => ({
          id: subLink.id,
          title: subLink.name,
          link: {
            itemKey: 'link',
            title: subLink.name,
            href: {
              id: 0,
              name: subLink.name || '',
              url: subLink.href,
              linkval: null,
              target: '_self',
            },
          },
        }));
      }
      return mappedItem;
    });
  }

  return (
    <div className="header-menu">
      <div
        ref={overlayRef}
        className={cx('header-menu-overlay', {
          'header-menu-overlay--active': isOpen && isLoaded,
        })}
      >
        <div
          ref={containerRef}
          className={cx('header-menu-container', {
            [`header-menu-container--level-${activeItems.length}`]:
              activeItems.length > 0,
          })}
        >
          {content?.searchPlaceholderText && (
            <button
              type="button"
              className="header-menu-search-button"
              onClick={() => {
                openSearch();
                onClose();
              }}
            >
              <Icon
                path={ICON_UI_SEARCH}
                size="s"
              />
              <span className="visually-hidden">
                {content?.searchPlaceholderText || 'Sök'}
              </span>
            </button>
          )}
          <button
            onClick={() => {
              onClose();
            }}
            type="button"
            className="header-menu-close-button"
          >
            <Icon path={ICON_UI_CLOSE} size="s" />
            <span className="visually-hidden">{content?.closeMenuLabel}</span>
          </button>
          <div className="header-menu-links">
            <ul className="header-main-menu">
              {mappedLinks?.map((mainLink) => {
                const { link, subLinks, title, id } = mainLink;
                const hasSubLinks = subLinks?.length > 0;

                if (hasSubLinks) {
                  const isExpanded = activeItems.find((item) => item.id === id);
                  return (
                    <li
                      className={cx('header-main-menu-item', {
                        'header-main-menu-item--expanded': isExpanded,
                      })}
                      key={id}
                    >
                      <button
                        type="button"
                        onClick={() => setActiveItems([{ title, id }])}
                      >
                        {title}
                        <Icon path={ICON_UI_CHEVRON_RIGHT} size="xs" />
                      </button>
                      <SubMenu
                        items={subLinks}
                        activeItems={activeItems}
                        setActiveItems={setActiveItems}
                        content={content}
                        parentId={id}
                      />
                    </li>
                  );
                }
                return (
                  <li className="header-main-menu-item" key={link?.title}>
                    <a href={link?.href?.url}>{link?.title}</a>
                  </li>
                );
              })}
            </ul>
            {isGlobetrotter && <div className="header-menu-divider" />}
            {isGlobetrotter && (
              <ul className="header-secondary-menu" ref={fadeRef}>
                {content.secondaryLinks.map((secondaryLink) => (
                  <li key={secondaryLink.title}>
                    <a href={secondaryLink.href.url}>{secondaryLink.title}</a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {!isGlobetrotter && (
          <div className="header-menu-fade" id="header-menu-fade" />
        )}
      </div>
    </div>
  );
};

Menu.propTypes = {
  isOpen: PropTypes.bool,
};

Menu.defaultProps = {
  isOpen: false,
};

export default Menu;
